import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { theCase, drugCards, persons, evidence } from '@/data';
import { Badge, type Tone } from '@/components/ui/Badge';
import { SectionCard } from '@/components/ui/Card';
import { FieldGrid } from '@/components/ui/FieldGrid';
import { Btn } from '@/components/ui/Button';
import { PageHeader } from '@/components/layout/PageHeader';

export default function CaseDetail() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();

  const c = theCase;
  const [tab, setTab] = useState('basic');
  const ln = (ko: string, mn: string) => (lang === 'ko' ? ko : mn);

  const custodyTone: Record<string, Tone> = {
    custodyArrested: 'danger',
    custodyWanted: 'warning',
    custodyReleased: 'neutral',
    custodyInvestigating: 'info',
  };
  const statusTone: Record<string, Tone> = {
    stInStorage: 'info',
    stAnalysis: 'warning',
    stDisposalReq: 'danger',
    stTransferred: 'neutral',
  };

  const tabs: [string, string][] = [
    ['basic', t('tabBasic')],
    ['drugcard', t('tabDrugCard')],
    ['persons', t('tabPersons')],
    ['evidence', t('tabEvidence')],
  ];

  const caseEvidence = evidence.filter((e) => e.case === c.no);

  return (
    <div className="mp-page" data-screen-label="사건 상세">
      <PageHeader
        crumbs={[t('home'), t('mInvest'), t('mCase'), t('detail')]}
        title={ln(c.nameKo, c.nameMn)}
        titleExtra={
          <div className="mp-pagehead__meta">
            <span className="mp-caseno">{c.no}</span>
            <Badge tone="primary">{t('caseStatusActive')}</Badge>
            <span className="mp-pagehead__updated">{t('lastUpdated')} {c.updated}</span>
          </div>
        }
        actions={
          <Fragment>
            <Btn icon="bi-printer">{t('print')}</Btn>
            <Btn icon="bi-pencil-square">{t('edit')}</Btn>
            <Btn kind="primary" icon="bi-diagram-3" onClick={() => navigate('/relation')}>{t('viewRelationMap')}</Btn>
          </Fragment>
        }
      />

      <div className="mp-tabs" role="tablist">
        {tabs.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id}
            className={'mp-tab' + (tab === id ? ' is-active' : '')}
            onClick={() => setTab(id)}>
            {label}
            {id === 'persons' ? <span className="mp-tab__count">{persons.length}</span> : null}
            {id === 'evidence' ? <span className="mp-tab__count">{caseEvidence.length}</span> : null}
            {id === 'drugcard' ? <span className="mp-tab__count">{drugCards.length}</span> : null}
          </button>
        ))}
      </div>

      {tab === 'basic' ? <TabBasic c={c} ln={ln} /> : null}
      {tab === 'drugcard' ? <TabDrugCard ln={ln} /> : null}
      {tab === 'persons' ? <TabPersons ln={ln} custodyTone={custodyTone} navigate={navigate} /> : null}
      {tab === 'evidence' ? <TabCaseEvidence ln={ln} rows={caseEvidence} statusTone={statusTone} navigate={navigate} /> : null}
    </div>
  );
}

interface TabBasicProps {
  c: typeof theCase;
  ln: (ko: string, mn: string) => string;
}

function TabBasic({ c, ln }: TabBasicProps) {
  const { t } = useTranslation();
  return (
    <div className="mp-grid mp-grid--main-side">
      <div className="mp-stack">
        <SectionCard title={t('tabBasic')}>
          <FieldGrid rows={[
            [t('caseNo'), <span className="mp-caseno">{c.no}</span>],
            [t('caseType'), t('caseTypeVal')],
            [t('receivedDate'), c.received],
            [t('occurredDate'), c.occurred],
            [t('occurredPlace'), ln(c.placeKo, c.placeMn)],
            [t('jurisdiction'), ln(c.jurisdictionKo, c.jurisdictionMn)],
            [t('assignedTeam'), ln(c.teamKo, c.teamMn)],
            [t('leadInvestigator'), c.lead],
            [t('coInvestigators'), c.co],
            [t('securityLevel'), <Badge tone="warning">{t('securityLevelVal')}</Badge>],
            [t('intlCoop'), t('intlCoopVal')],
          ]} />
        </SectionCard>
        <SectionCard title={t('overview')}>
          <p className="mp-overview">{t('caseOverviewText')}</p>
        </SectionCard>
      </div>
      <div className="mp-stack">
        <SectionCard title={t('progressHistory')} flush>
          <ol className="mp-timeline">
            {c.history.map((h, i) => (
              <li key={i} className={i === c.history.length - 1 ? 'is-last' : ''}>
                <span className="mp-timeline__date">{h.date}</span>
                <span className="mp-timeline__text">{t(h.key)}</span>
              </li>
            ))}
          </ol>
        </SectionCard>
        <SectionCard title={t('relatedRequests')} flush>
          <ul className="mp-linklist">
            <li>
              <i className="bi bi-flask"></i>
              <span>ХС-2026-0034 — {t('analysisDone')}</span>
              <Badge tone="success">{t('analysisDone')}</Badge>
            </li>
            <li>
              <i className="bi bi-cpu"></i>
              <span>ДФ-2026-0012 — Galaxy S24</span>
              <Badge tone="warning">{t('analysisPending')}</Badge>
            </li>
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}

interface TabDrugCardProps {
  ln: (ko: string, mn: string) => string;
}

function TabDrugCard({ ln }: TabDrugCardProps) {
  const { t } = useTranslation();
  return (
    <div className="mp-stack">
      {drugCards.map((card) => (
        <SectionCard key={card.no}
          title={<span><i className="bi bi-card-heading mp-card__titleicon"></i>{t('drugCardTitle')} <span className="mp-caseno">{card.no}</span></span>}
          extra={card.analysis === 'done'
            ? <Badge tone="success">{t('analysisDone')}</Badge>
            : <Badge tone="warning">{t('analysisPending')}</Badge>}>
          <div className="mp-drugcard">
            <div className="mp-drugcard__hero">
              <div className="mp-drugcard__substance">{card.substance}</div>
              <div className="mp-drugcard__amount">{card.amount}</div>
              <div className="mp-drugcard__value">{t('streetValue')} {card.value}</div>
            </div>
            <FieldGrid rows={[
              [t('substanceName'), card.substance],
              [t('purity'), card.purity],
              [t('form'), t('formVal')],
              [t('packaging'), t('packagingVal')],
              [t('seizureDate'), card.seizureDate],
              [t('seizurePlace'), ln(card.placeKo, card.placeMn)],
              [t('inboundRoute'), t('inboundRouteVal')],
              [t('distributionMethod'), t('distributionMethodVal')],
              [t('cardCreated'), card.created],
              [t('analysisStatus'), card.analysis === 'done' ? t('analysisDone') : t('analysisPending')],
            ]} />
          </div>
        </SectionCard>
      ))}
    </div>
  );
}

interface TabPersonsProps {
  ln: (ko: string, mn: string) => string;
  custodyTone: Record<string, Tone>;
  navigate: ReturnType<typeof useNavigate>;
}

function TabPersons({ ln, custodyTone, navigate }: TabPersonsProps) {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={t('tabPersons')}
      extra={<Btn small icon="bi-person-plus">{t('register')}</Btn>}
      flush>
      <table className="mp-table">
        <thead>
          <tr>
            <th>{t('name')}</th>
            <th>{t('personRole')}</th>
            <th>{t('personDob')}</th>
            <th>{t('personAddress')}</th>
            <th>{t('personPhone')}</th>
            <th>{t('priorRecord')}</th>
            <th>{t('personStatus')}</th>
            <th>{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {persons.map((p) => (
            <tr key={p.id}>
              <td className="mp-table__strong">{p.name}</td>
              <td>{t(p.role)}</td>
              <td>{p.dob}</td>
              <td>{ln(p.addrKo, p.addrMn)}</td>
              <td>{p.phone}</td>
              <td>{t(p.prior)}</td>
              <td><Badge tone={custodyTone[p.custody]}>{t(p.custody)}</Badge></td>
              <td>
                <button type="button" className="mp-tablelink" onClick={() => navigate('/relation')}>
                  <i className="bi bi-diagram-3"></i> {t('viewRelationMap')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}

interface TabCaseEvidenceProps {
  ln: (ko: string, mn: string) => string;
  rows: (typeof evidence)[number][];
  statusTone: Record<string, Tone>;
  navigate: ReturnType<typeof useNavigate>;
}

function TabCaseEvidence({ ln, rows, statusTone, navigate }: TabCaseEvidenceProps) {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={t('tabEvidence')}
      extra={
        <div className="mp-btnrow">
          <Btn small icon="bi-box-arrow-in-down">{t('registerEvidence')}</Btn>
          <Btn small icon="bi-arrow-left-right" onClick={() => navigate('/evidence')}>{t('mEvidence')}</Btn>
        </div>
      }
      flush>
      <table className="mp-table">
        <thead>
          <tr>
            <th>{t('evidenceNo')}</th>
            <th>{t('evidenceName')}</th>
            <th>{t('evidenceCategory')}</th>
            <th>{t('weight')}</th>
            <th>{t('storageLocation')}</th>
            <th>{t('storageStatus')}</th>
            <th>{t('registeredDate')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.no}>
              <td><span className="mp-caseno">{e.no}</span></td>
              <td className="mp-table__strong">{ln(e.nameKo, e.nameMn)}</td>
              <td>{t(e.cat)}</td>
              <td>{e.weight}</td>
              <td>{e.loc}</td>
              <td><Badge tone={statusTone[e.status]}>{t(e.status)}</Badge></td>
              <td>{e.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}
